import { QuantityUnitsService } from './../../../@core/utils/quantity-units.service';
import { combineLatest, Subscription, switchMap } from 'rxjs';
import { UsersService } from './../../../@core/utils/users.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { KPI } from '../../../@core/api';
import { AutoPpaService, I18nService, PriceSheetService } from '../../../@core/utils';
import { PriceTier } from '../../../@core/utils/auto-ppa.service';
import { DatePipe } from '@angular/common';
import { NbDialogService, NbListModule, NbSelectModule } from '@nebular/theme';
import { PriceSheetDetailsDialogComponent } from '../price-sheet-details-dialog/price-sheet-details-dialog.component';
import { UsernamePipe } from '../../pipes/username.pipe';
import { ThousandsSeparatorPipe } from '../../pipes/thousands-separator.pipe';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GROUP_PERMISSION_EDIT_PRICE_SHEET } from '../../../@core/api/models';
import { PriceSheetFormComponent } from '../side-forms/price-sheet-form/price-sheet-form.component';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-price-sheets-list',
    styleUrls: ['./price-sheets-list.component.scss'],
    templateUrl: './price-sheets-list.component.html',
    standalone: true,
    imports: [
    NbListModule,
    FontAwesomeModule,
    TranslateModule,
    CurrencyPipe,
    ThousandsSeparatorPipe,
    UsernamePipe,
    NbSelectModule,
    FormsModule
],
})
export class PriceSheetsListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public priceSheets: KPI.PriceSheet[];
  @Input() public selectedPriceSheetId: number;
  @Input() public assetType: KPI.AssetType;
  @Input() public asset: KPI.Asset;
  @Input() public currentAssetId: number;
  @Input() public assetQuantity: number;
  @Input() public isPPA: boolean = false
  @Input() public showIsDefault: boolean = true;
  @Input() public automaticCheckDefaultPriceSheet: boolean = true;
  @Input() private readOnly: boolean = false;
  @Input() public inWizard: boolean = false;

  @Output() priceSheetsChanges = new EventEmitter<number>();
  @Output() closeAfterChanges = new EventEmitter<void>();
  @Output() customerPsDeleted = new EventEmitter<boolean>();
  @Output() priceSheetSelected = new EventEmitter<KPI.PriceSheet>();
  @Output() priceTierChanged = new EventEmitter<PriceTier>();

  public currency: string = this.i18n.getCurrency();
  public quantityUnits: { [id: number]: string } = {}
  public priceSheetUserNames: { [id: number]: string } = {};
  public newlySelectedPriceSheetId: number;
  public selectedPriceTier: PriceTier = 'med';
  private pipe: DatePipe;
  private subscriptions: Subscription[] = [];
  public canEdit: boolean;

  constructor(
    private i18n: I18nService,
    private translate: TranslateService,
    private usersService: UsersService,
    private quantityUnitsService: QuantityUnitsService,
    private dialogService: NbDialogService,
    private rightPanelDialogService: RightPanelDialogService,
    private autoPPAService: AutoPpaService,
    private priceSheetsService: PriceSheetService,
  ) {
    this.pipe = new DatePipe(this.translate.getBrowserCultureLang());
  }

  ngOnInit(): void {
    if (this.currentAssetId) {
      this.subscriptions.push(
        this.autoPPAService.autoPPA$.subscribe((autoPPA) => {
          this.selectedPriceTier = autoPPA[this.currentAssetId]?.selected_price_tier || 'med';
        })
      );
    }
    this.subscriptions.push(
      combineLatest([
        this.quantityUnitsService.quantityUnits$,
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_PRICE_SHEET])
      ]).subscribe(([quantityUnits, canEdit]) => {
        quantityUnits.forEach((quantityUnit) => this.quantityUnits[quantityUnit.id] = quantityUnit.name);
        this.canEdit = canEdit && !this.readOnly;
      }),
      this.priceSheetsService._pricesheetsHaveChanged$.subscribe(() => {
        this.priceSheetsService.getPriceSheetsByAssetType(this.assetType.id).subscribe((priceSheets) => {
          this.priceSheets = priceSheets;
        })
      })
    );
  }

  public selectPriceTier(tier: PriceTier, priceSheet: KPI.PriceSheet): void {
    this.selectedPriceTier = tier;
    this.priceTierChanged.emit(tier);
    this.priceSheetSelected.emit(priceSheet);
  }

  ngOnChanges(): void {
    if (this.priceSheets?.length) {
      this.priceSheets.forEach((priceSheet) => {
        const date = this.pipe.transform(priceSheet.modified_on, 'd MMM y');
        const userName = this.usersService.getUserName(priceSheet.author);
        if (this.isPPA) {
          if (this.isPriceSheetSelected(priceSheet.id)) {
            this.selectedPriceSheetId = priceSheet.id
          }
        }
        this.priceSheetUserNames[priceSheet.id] = this.translate.instant(
          'Updated on {date} by {name}', { name: userName, date: date },
        );
      })
    }
  }

  public openDetails(priceSheet: KPI.PriceSheet, assetType: KPI.AssetType): void {
    const dialogRef = this.dialogService.open(PriceSheetDetailsDialogComponent, {
      context: {
        priceSheetId: priceSheet.id,
        assetTypeId: assetType.id,
        currentAssetId: this.currentAssetId,
      },
      autoFocus: false,
    });
    if (this.currentAssetId) {
      dialogRef.onClose.subscribe((result) => {
        if (result?.selectedPriceTier) {
          this.selectedPriceTier = result.selectedPriceTier;
          this.priceTierChanged.emit(result.selectedPriceTier);
        }
      });
    }
  }

  public updateSelectedPriceSheet(priceSheet: KPI.PriceSheet, is_default: boolean) {
    if (!is_default && !this.inWizard) {
      const currentAssetId = this.currentAssetId;
      const newlySelectedPriceSheetId = priceSheet.id;
      this.autoPPAService.updateSelectedPriceSheet(currentAssetId, newlySelectedPriceSheetId);
    }
    this.selectedPriceSheetId = priceSheet.id;
    this.priceSheetSelected.emit(priceSheet);
  }

  public changeDefaultPricesheet(priceSheetId: number) {
    this.priceSheetsService.changeDefaultPriceSheet(priceSheetId).pipe(
      switchMap((ps) =>
        this.priceSheetsService.getPriceSheetsByAssetType(ps.asset_type)
      )
    ).subscribe((priceSheets) => {
      this.priceSheets = priceSheets;
      this.priceSheetsService.defaultPSHasChanged();
    })
  }

  private isPriceSheetSelected(priceSheetId: number): boolean {
    const selectedPriceSheet = this.autoPPAService.getPriceSheetMappings();
    if (selectedPriceSheet.length) {
      for (const item of selectedPriceSheet) {
        if (item.asset === this.currentAssetId && item.pricesheet === priceSheetId) {
          return true;
        }
      }
      return false;
    } else {
      return false
    }
  }

  public deletePricesheet(pricesheet: KPI.PriceSheet): void {
    if(!pricesheet.is_in_use) {
      this.priceSheetsService.deletePriceSheet(pricesheet.id).subscribe(() => {
        if (pricesheet.is_customer) {
          this.customerPsDeleted.emit(true)
        }
        if (this.priceSheets.length > 1) {
          const deletedPriceSheet = this.priceSheets.findIndex((ps) => ps.id === pricesheet.id);
          if (deletedPriceSheet !== -1) {
            this.priceSheets.splice(deletedPriceSheet, 1);
          }
          this.priceSheetsChanges.emit(this.priceSheets.length)
        } else {
          this.closeAfterChanges.emit()
        }
        this.priceSheetsService.pricesheetsHaveChanged();
      })
    } else {
      return
    }
  }

  public updatePriceSheet(priceSheet: KPI.PriceSheet) {
    this.rightPanelDialogService.open(
      PriceSheetFormComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          assetType: this.assetType,
          priceSheet: priceSheet,
        },
      },
    )
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  /**
   * One more case of "data does't match its supposed type -_-'"
   */
  public isPriceTierDisabled(priceTierValue: any) {
    if (typeof priceTierValue === "number") {
      return priceTierValue <= 0;
    }
    if (typeof priceTierValue === "string") {
      return parseInt(priceTierValue) <= 0;
    }
  }
}

